import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Alert, CircularProgress,
    Avatar, Chip, Card, CardContent, Divider,
} from '@mui/joy';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axiosConfig from '../util/axiosConfig';

const ORANGE = '#eb7a24';

const CITY_MAP = {
    hyd: 'Hyderabad', blr: 'Bangalore', chn: 'Chennai', pun: 'Pune',
    coc: 'Cochin', bhb: 'Bhubaneswar', del: 'Delhi', ahm: 'Ahmedabad',
    kol: 'Kolkata', mum: 'Mumbai', bar: 'Baroda', gur: 'Gurgaon',
    noi: 'Noida', chd: 'Chandigarh', ind: 'Indore', coi: 'Coimbatore',
    jai: 'Jaipur', tri: 'Trivandrum', bhp: 'Bhopal',
};
// DB may store city names (e.g. "hyderabad") in places/rplaces columns; map them to codes
const CITY_NAME_TO_CODE = {
    hyderabad: 'hyd', bangalore: 'blr', chennai: 'chn', pune: 'pun',
    cochin: 'coc', bhubaneswar: 'bhb', delhi: 'del', ahmedabad: 'ahm',
    kolkata: 'kol', mumbai: 'mum', baroda: 'bar', gurgaon: 'gur',
    noida: 'noi', chandigarh: 'chd', indore: 'ind', coimbatore: 'coi',
    jaipur: 'jai', trivandrum: 'tri', bhopal: 'bhp',
};
const toCode = (s) => { const l = s.trim().toLowerCase(); return CITY_NAME_TO_CODE[l] || l; };

const SALARY_LABELS = {
    '1': '0.6 LPA to 0.99 LPA', '2': '1.0 LPA to 1.49 LPA',
    '3': '1.5 LPA to 1.99 LPA', '4': '2.0 LPA to 2.49 LPA',
    '5': '2.5 LPA to 2.99 LPA', '6': '3.0 LPA & Above',
    '7': '3.5+', '8': '4.0+', '9': '4.5+', '10': '5.0+',
};

const parseCityPrefs = (pd) => {
    const salaryMap = {};
    (pd.salary || '').split(',').filter(Boolean).forEach(part => {
        const [code, sal] = part.split(':');
        if (code) salaryMap[code.trim()] = (sal || '').trim();
    });
    const placesSet = new Set((pd.places || '').split(',').filter(Boolean).map(toCode));
    const relocateSet = new Set((pd.rplaces || '').split(',').filter(Boolean).map(toCode));

    // Union of all cities mentioned across places/salary/rplaces
    const allCodes = new Set([...placesSet, ...Object.keys(salaryMap), ...relocateSet]);
    return Array.from(allCodes)
        .filter(code => CITY_MAP[code])
        .map(code => ({
            code,
            name: CITY_MAP[code],
            salary: salaryMap[code] || '',
            writtenTest: placesSet.has(code),
            relocate: relocateSet.has(code),
        }));
};

const PlacementProfile = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [placementData, setPlacementData] = useState(null);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [profileRes, placementRes] = await Promise.all([
                    axiosConfig.get('/students/profile'),
                    axiosConfig.get('/students/placement-profile'),
                ]);
                setProfileData(profileRes.data);
                setPlacementData(placementRes.data);
                setPhotoUrl(profileRes.data?.photoUrl || null);
            } catch (err) {
                setError(err.response?.data?.errorMessage || 'Failed to load placement profile');
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
        return () => {};
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: 2 }}>
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Box sx={{ p: 2 }}><Alert color="danger" variant="soft">{error}</Alert></Box>;
    }

    const p = profileData || {};
    const pd = placementData || {};
    const isAvailable = pd.placement === true || pd.placement === 1;
    const cityPrefs = isAvailable ? parseCityPrefs(pd) : [];

    return (
        <Box sx={{ p: 2, maxWidth: '1100px', mx: 'auto', width: '100%' }}>

            {/* Page title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WorkIcon sx={{ color: ORANGE, fontSize: '2rem' }} />
                <Typography level="h2" sx={{ color: ORANGE, fontWeight: 'bold' }}>
                    Placement Profile
                </Typography>
            </Box>

            {/* Profile header card */}
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Avatar
                            src={photoUrl || undefined}
                            alt={p.name || 'Student'}
                            sx={{
                                width: 68, height: 68,
                                border: `2px solid ${ORANGE}`,
                                bgcolor: '#f19b30',
                                fontSize: '1.6rem',
                                '& img': { objectFit: 'cover', objectPosition: 'center' },
                            }}
                        >
                            {p.name ? p.name.charAt(0).toUpperCase() : 'S'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 160 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Typography level="h4" sx={{ color: '#404041' }}>{p.name || 'Student'}</Typography>
                                {(p.studentId || p.studentid) && (
                                    <Typography level="h4" sx={{ color: ORANGE, fontWeight: 'bold' }}>
                                        {p.studentId || p.studentid}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Placement preferences card */}
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, mb: 2, borderBottom: `2px solid ${ORANGE}` }}>
                        <WorkIcon sx={{ color: ORANGE, fontSize: '1.2rem', mr: 0.75 }} />
                        <Typography level="title-md" sx={{ color: ORANGE, fontWeight: 'bold' }}>
                            Placement Preferences
                        </Typography>
                        <Box sx={{ ml: 'auto' }}>
                            <Chip
                                size="sm"
                                variant="solid"
                                sx={{
                                    bgcolor: isAvailable ? '#2e7d32' : '#c62828',
                                    color: '#fff',
                                    fontWeight: 700,
                                }}
                            >
                                {isAvailable ? 'Available for Placement' : 'Not Available for Placement'}
                            </Chip>
                        </Box>
                    </Box>

                    {!isAvailable ? (
                        <Typography level="body-md" sx={{ color: '#757575' }}>
                            Not available for placement.
                        </Typography>
                    ) : cityPrefs.length === 0 ? (
                        <Typography level="body-md" sx={{ color: '#9e9e9e' }}>
                            No location preferences saved yet.
                        </Typography>
                    ) : (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                            gap: 1.5,
                        }}>
                            {cityPrefs.map(({ code, name, salary, writtenTest, relocate }) => (
                                <Box key={code} sx={{
                                    border: '1px solid',
                                    borderColor: ORANGE,
                                    borderRadius: 'md',
                                    p: 1.5,
                                    bgcolor: '#fff8f3',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.25 }}>
                                        <LocationOnIcon sx={{ color: ORANGE, fontSize: '1rem' }} />
                                        <Typography level="body-sm" sx={{ fontWeight: 700, color: ORANGE }}>
                                            {name}
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ mb: 1 }} />

                                    {/* Expected Salary */}
                                    <Typography level="body-xs" sx={{ color: '#6e7072', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Expected Salary
                                    </Typography>
                                    <Box sx={{ mb: 1.25 }}>
                                        {salary ? (
                                            <Chip
                                                size="sm"
                                                variant="solid"
                                                sx={{ bgcolor: ORANGE, color: '#fff', fontWeight: 700 }}
                                            >
                                                {SALARY_LABELS[salary] || `${salary} LPA`}
                                            </Chip>
                                        ) : (
                                            <Typography level="body-xs" sx={{ color: '#9e9e9e' }}>—</Typography>
                                        )}
                                    </Box>

                                    {/* Written Test */}
                                    <Typography level="body-xs" sx={{ color: '#6e7072', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Written Test
                                    </Typography>
                                    <Box sx={{ mb: 1.25 }}>
                                        <Chip
                                            size="sm"
                                            variant="solid"
                                            sx={{ bgcolor: writtenTest ? '#2e7d32' : '#757575', color: '#fff', fontWeight: 700 }}
                                        >
                                            {writtenTest ? 'Yes' : 'No'}
                                        </Chip>
                                    </Box>

                                    {/* Willing to Relocate */}
                                    <Typography level="body-xs" sx={{ color: '#6e7072', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Willing to Relocate
                                    </Typography>
                                    <Chip
                                        size="sm"
                                        variant="solid"
                                        sx={{
                                            bgcolor: relocate ? '#2e7d32' : '#757575',
                                            color: '#fff',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {relocate ? 'Yes' : 'No'}
                                    </Chip>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {pd.time && (
                        <Typography level="body-xs" sx={{ color: '#9e9e9e', mt: 2, textAlign: 'right' }}>
                            {pd.updateType}: {pd.time}
                        </Typography>
                    )}
                </CardContent>
            </Card>

        </Box>
    );
};

export default PlacementProfile;
